import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';

import {
  ArrowSquareOut,
  CheckCircle,
  CircleNotch,
  WarningCircle
} from '@phosphor-icons/react';
const AgentActivationModal = ({ agent, onClose, onSuccess }) => {
  const { activeOrganization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPricingInfo();
  }, []);

  const fetchPricingInfo = async () => {
    // Determine if this is a voice agent based on multiple fields
    const isVoice = agent.is_voice_agent || 
                    agent.voice_mode === 'live_s2s' || 
                    agent.is_voice_template ||
                    agent.capability_profile === 'live_voice_s2s_agent' ||
                    agent.capability_profile === 'voice_input_agent' ||
                    agent.capability_profile === 'voice_output_tts_agent';
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/agents/${agent.id}/billing/status`, {
        headers: {
          'X-Organization-Id': activeOrganization?.organization_id
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Override price if backend returns wrong value but we know it's a voice agent
        if (isVoice && data.price_cents < 4900) {
          data.price_cents = 4900;
        }
        setPricingInfo(data);
      } else {
        // Default pricing if endpoint fails - $49 for voice agents, $29 for standard
        setPricingInfo({ price_cents: isVoice ? 4900 : 2900, currency: 'USD' });
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
      // Default pricing - $49 for voice agents, $29 for standard
      setPricingInfo({ price_cents: isVoice ? 4900 : 2900, currency: 'USD' });
    }
  };

  const handleActivate = async () => {
    setLoading(true);
    setError('');

    const isVoiceAgent = agent.is_voice_agent || 
                         agent.voice_mode === 'live_s2s' || 
                         agent.is_voice_template ||
                         agent.capability_profile === 'live_voice_s2s_agent' ||
                         agent.capability_profile === 'voice_input_agent' ||
                         agent.capability_profile === 'voice_output_tts_agent';

    try {
      // Phase 8: If hosting is free, use the bypass activation endpoint directly
      if (isHostingFree) {
        const response = await fetch(`${BACKEND_URL}/api/agents/${agent.id}/billing/activate-bypass`, {
          method: 'POST',
          headers: {
            'X-Organization-Id': activeOrganization?.organization_id
          },
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to activate agent');
        }

        if (onSuccess) onSuccess();
        onClose();
        return;
      }

      // Original Stripe checkout flow (runs only when hosting fees are enabled)
      const response = await fetch(`${BACKEND_URL}/api/stripe/create-agent-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': activeOrganization?.organization_id
        },
        credentials: 'include',
        body: JSON.stringify({
          agent_id: agent.id,
          agent_name: agent.name,
          is_voice_agent: isVoiceAgent,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error('Error activating agent:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  if (!pricingInfo) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
        <div className="glass rounded-3xl p-8 max-w-lg w-full flex items-center justify-center">
          <CircleNotch size={48} className="animate-spin text-[#6366F1]" />
        </div>
      </div>
    );
  }

  const priceDollars = (pricingInfo.price_cents / 100).toFixed(2);
  const isHostingFree = pricingInfo.hosting_fee_enabled === false || pricingInfo.price_cents === 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass rounded-3xl p-8 max-w-lg w-full"
      >
        <h2 className="text-3xl font-bold text-white mb-2">
          {isHostingFree ? 'Activate Agent' : 'Activate Agent Hosting'}
        </h2>
        <p className="text-[#A0AEC0] mb-6">
          {agent.name}
        </p>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-[#EF4444]/20 border border-[#EF4444]/30">
            <div className="flex items-start gap-3">
              <WarningCircle size={20} className="text-[#EF4444] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[#EF4444] font-semibold mb-1">Error</p>
                <p className="text-[#EF4444] text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Card */}
        <div className="glass rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[#A0AEC0] text-sm mb-1">{isHostingFree ? 'Hosting' : 'Monthly Hosting Fee'}</p>
              <div className="flex items-baseline gap-2">
                {isHostingFree ? (
                  <p className="text-4xl font-bold text-[#14F195]">Free</p>
                ) : (
                  <>
                    <p className="text-4xl font-bold text-white">${priceDollars}</p>
                    <p className="text-[#A0AEC0] text-sm">/month</p>
                  </>
                )}
              </div>
              {isHostingFree && (
                <p className="text-[#A0AEC0] text-xs mt-1">AI compute powered by credits</p>
              )}
            </div>
          </div>

          <div className="space-y-2 text-sm text-[#A0AEC0]">
            <div className="flex items-center justify-between">
              <span>&#10003; 24/7 hosted infrastructure</span>
            </div>
            <div className="flex items-center justify-between">
              <span>&#10003; Automatic scaling</span>
            </div>
            <div className="flex items-center justify-between">
              <span>&#10003; API access &amp; monitoring</span>
            </div>
          </div>
        </div>

        {/* What Happens Next */}
        <div className="mb-6 p-4 rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/20">
          <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
            <CheckCircle size={18} className="text-[#6366F1]" />
            What happens next?
          </h3>
          {isHostingFree ? (
            <ul className="space-y-1 text-sm text-[#A0AEC0]">
              <li>1. Your agent will be activated instantly</li>
              <li>2. No payment required — hosting is free</li>
              <li>3. AI compute uses your plan&apos;s monthly credits</li>
              <li>4. Use your own LLM keys for zero credit usage</li>
            </ul>
          ) : (
            <ul className="space-y-1 text-sm text-[#A0AEC0]">
              <li>1. You&apos;ll be redirected to secure Stripe checkout</li>
              <li>2. Enter your payment details</li>
              <li>3. Agent activates automatically after payment</li>
              <li>4. Billed monthly, cancel anytime</li>
            </ul>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleActivate}
            disabled={loading}
            className="flex-1 px-6 py-3 rounded-full bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <CircleNotch size={20} className="animate-spin" />
                {isHostingFree ? 'Activating...' : 'Processing...'}
              </>
            ) : isHostingFree ? (
              'Activate Now — Free'
            ) : (
              <>
                Activate for ${priceDollars}/month
                <ArrowSquareOut size={18} />
              </>
            )}
          </button>
        </div>

        {/* Secure Payment Badge - only show when payment is needed */}
        {!isHostingFree && (
          <div className="mt-6 text-center">
            <p className="text-[#A0AEC0] text-xs flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Secure payment powered by Stripe
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AgentActivationModal;
